/**
 * ChatProgress — 3px sienna bar + mono meta line.
 *
 * Matches the prototype's chat header progress: thin track over Cream,
 * sienna fill, with "Question N of M" + "X% covered" beneath in Geist Mono.
 */

import { cn } from '@/lib/utils';

interface ChatProgressProps {
  /** Current question index (1-based). When omitted, falls back to currentSection. */
  questionIdx?: number;
  /** Total questions. Defaults to 5 to match the prototype. */
  questionTotal?: number;
  /** Section name from the Scope Agent — shown only when questionIdx is unset. */
  currentSection?: string;
  /** Progress percentage (0–100). */
  progressPercent?: number;
  className?: string;
}

export function ChatProgress({
  questionIdx,
  questionTotal = 5,
  currentSection,
  progressPercent = 0,
  className,
}: ChatProgressProps) {
  const clamped = Math.min(100, Math.max(0, Math.round(progressPercent)));
  const leftLabel = questionIdx
    ? `Question ${questionIdx} of ${questionTotal}`
    : currentSection ?? '';

  return (
    <div className={cn('mt-2.5', className)}>
      <div
        className="h-[3px] overflow-hidden rounded-full"
        style={{ background: 'hsl(var(--muted))' }}
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Interview progress: ${clamped}%`}
      >
        <div
          className="h-full rounded-full transition-[width] duration-700 ease-out"
          style={{
            width: `${clamped}%`,
            background: 'hsl(var(--sienna))',
          }}
        />
      </div>
      <div
        className="mt-1.5 flex items-center justify-between kv-eyebrow text-[10px] tabular-nums"
        style={{ color: 'hsl(var(--muted-foreground))' }}
      >
        <span className="truncate">{leftLabel}</span>
        <span>{clamped}% covered</span>
      </div>
    </div>
  );
}
